import React from 'react';
import { AlertCircle, AtSign, CalendarClock, CalendarDays, CheckCircle2, FileText, Send } from 'lucide-react';
import { Kpis, isAllClear, shortDayLabel, summarizeStatus } from './dashboardModel';
import { TEXT_TONE, Tone } from './dashboardTones';

export type KpiKey = 'overdue' | 'today' | 'week' | 'tasks' | 'unread' | 'unbooked';

interface Props {
    kpis: Kpis;
    onSelect: (key: KpiKey) => void;
    /** Handy: Reihen zu drei Kacheln. */
    mobile?: boolean;
    /** Welche Kacheln, in dieser Reihenfolge (persoenliches Layout); ohne Angabe alle sechs. */
    tiles?: ReadonlyArray<KpiKey>;
}

interface TileSpec {
    key: KpiKey;
    label: string;
    /** Kurzform fuer drei Kacheln nebeneinander auf 360 px. */
    shortLabel: string;
    value: number | null;
    sub: string;
    shortSub: string;
    /** Farbe nur, wenn der Wert etwas bedeutet (> 0). */
    tone: Tone;
    icon: React.ReactNode;
    zeroText: string;
    shortZeroText: string;
}

const Tile: React.FC<{ t: TileSpec; onClick: () => void; mobile: boolean }> = ({ t, onClick, mobile }) => {
    const loading = t.value === null;
    const zero = t.value === 0;
    const tone = zero || loading ? 'slate' : t.tone;
    const sub = loading ? '…' : zero ? (mobile ? t.shortZeroText : t.zeroText) : (mobile ? t.shortSub : t.sub);
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={loading}
            data-kpi-tile={t.key}
            data-tone={tone}
            aria-label={`${t.label}: ${loading ? 'wird geladen' : `${t.value} — ${zero ? t.zeroText : t.sub}`}`}
            className={`group text-left bg-white rounded-xl border border-slate-200 hover:border-slate-300 active:bg-slate-50 transition-colors min-w-0 disabled:cursor-default flex flex-col justify-between ${mobile ? 'px-2.5 py-2 min-h-[72px]' : 'px-3 py-2.5 min-h-[76px]'}`}
        >
            <div className="flex items-center justify-between gap-1.5 min-w-0">
                <span className={`text-[11px] font-semibold tracking-wide text-slate-500 truncate ${mobile ? '' : 'uppercase'}`}>{mobile ? t.shortLabel : t.label}</span>
                {!mobile && <span className={`shrink-0 [&>svg]:w-3.5 [&>svg]:h-3.5 ${zero ? 'text-slate-300' : TEXT_TONE[tone]}`}>{t.icon}</span>}
            </div>
            {mobile ? (
                <div className="mt-1 min-w-0">
                    {loading ? (
                        <span className="skeleton block h-6 w-10 rounded-md" aria-hidden="true" />
                    ) : (
                        <span className={`block text-2xl font-semibold leading-none tabular ${zero ? 'text-slate-300' : TEXT_TONE[tone]}`}>{t.value}</span>
                    )}
                    <span className={`block text-[11px] leading-tight truncate mt-1 ${zero ? 'text-slate-400' : 'text-slate-500'}`}>{sub}</span>
                </div>
            ) : (
                <div className="flex items-end justify-between gap-2 mt-1 min-w-0">
                    {loading ? (
                        <span className="skeleton h-6 w-10 rounded-md" aria-hidden="true" />
                    ) : (
                        <span className={`text-2xl font-semibold leading-none tabular ${zero ? 'text-slate-300' : TEXT_TONE[tone]}`}>{t.value}</span>
                    )}
                    <span className={`text-[11px] leading-tight text-right truncate ${zero ? 'text-slate-400' : 'text-slate-500'}`}>{sub}</span>
                </div>
            )}
        </button>
    );
};

/** Handy: 1, 2 und 4 Kacheln fuellen die Reihen, alles andere laeuft zu dritt. */
const mobileCols = (n: number): string => (n === 1 ? 'grid-cols-1' : n === 2 || n === 4 ? 'grid-cols-2' : 'grid-cols-3');

/**
 * Die Kachelreihe „Heute": bis zu sechs Zahlen, jede klickbar. Feste Hoehe,
 * damit nichts springt, wenn Daten eintreffen; Nullen werden leise (grau)
 * statt laut, und die Kopfzeile sagt in einem Satz, ob etwas ansteht.
 *
 * Die Leiste kann im persoenlichen Layout in jeder Zone stehen — ueber die
 * ganze Breite oder in der schmalen Seitenspalte. Deshalb richtet sich das
 * Raster nach der eigenen Breite (Container Query), nicht nach dem Fenster:
 * zwei Kacheln je Reihe, ab 512 px drei, ab 768 px alle nebeneinander.
 */
export const KpiStrip: React.FC<Props> = ({ kpis, onSelect, mobile = false, tiles: selected }) => {
    const clear = isAllClear(kpis);
    const weekEnd = shortDayLabel(kpis.weekEnd);
    const all: TileSpec[] = [
        { key: 'overdue', label: 'Überfällig', shortLabel: 'Überfällig', value: kpis.overdue, sub: 'Begehungen', shortSub: 'Begehungen', tone: 'red', icon: <AlertCircle />, zeroText: 'nichts offen', shortZeroText: 'nichts offen' },
        { key: 'today', label: 'Heute', shortLabel: 'Heute', value: kpis.today, sub: 'fällig', shortSub: 'fällig', tone: 'amber', icon: <CalendarClock />, zeroText: 'nichts fällig', shortZeroText: 'nichts fällig' },
        { key: 'week', label: 'Diese Woche', shortLabel: 'Woche', value: kpis.week, sub: `bis ${weekEnd}`, shortSub: `bis ${weekEnd.split(',')[0]}`, tone: 'blue', icon: <CalendarDays />, zeroText: `frei bis ${weekEnd}`, shortZeroText: 'frei' },
        {
            key: 'tasks', label: 'Aufgaben', shortLabel: 'Aufgaben', value: kpis.tasksOpen,
            sub: kpis.tasksUrgent > 0 ? `${kpis.tasksUrgent} dringend` : 'offen', shortSub: kpis.tasksUrgent > 0 ? `${kpis.tasksUrgent} dringend` : 'offen',
            tone: kpis.tasksUrgent > 0 ? 'red' : 'slate', icon: <FileText />, zeroText: 'alles erledigt', shortZeroText: 'erledigt',
        },
        { key: 'unread', label: 'Mitteilungen', shortLabel: 'Ungelesen', value: kpis.unread, sub: 'ungelesen', shortSub: 'Mitteilungen', tone: 'blue', icon: <AtSign />, zeroText: 'nichts Neues', shortZeroText: 'nichts Neues' },
        { key: 'unbooked', label: 'Nicht gebucht', shortLabel: 'Ungebucht', value: kpis.unbooked, sub: 'versendete Berichte', shortSub: 'Berichte', tone: 'amber', icon: <Send />, zeroText: 'alles gebucht', shortZeroText: 'alles gebucht' },
    ];

    const byKey = new Map(all.map(t => [t.key, t] as const));
    const tiles = selected ? selected.flatMap(k => { const t = byKey.get(k); return t ? [t] : []; }) : all;
    if (tiles.length === 0) return null;

    return (
        <section aria-label="Heute im Blick" className="min-w-0 @container">
            <div className={`mb-1.5 min-w-0 ${mobile ? 'space-y-0.5' : 'flex items-center justify-between gap-3 @max-lg:block @max-lg:space-y-0.5'}`}>
                <h2 className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 shrink-0">Heute im Blick</h2>
                <p className={`text-[11px] flex items-start gap-1 min-w-0 ${clear ? 'text-emerald-700 font-semibold' : 'text-slate-500'}`}>
                    {clear && <CheckCircle2 className="w-3 h-3 shrink-0 mt-0.5" />}
                    <span className={mobile ? 'line-clamp-2' : 'truncate'}>{summarizeStatus(kpis)}</span>
                </p>
            </div>
            <div className={`grid gap-2 ${mobile ? mobileCols(tiles.length) : 'grid-cols-2 @lg:grid-cols-3 @3xl:grid-cols-none @3xl:grid-flow-col @3xl:auto-cols-fr'}`}>
                {tiles.map(t => <Tile key={t.key} t={t} mobile={mobile} onClick={() => onSelect(t.key)} />)}
            </div>
        </section>
    );
};
